import { useQuery } from "@tanstack/react-query";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Shell } from "./components/Shell";
import { Loading } from "./components/ui";
import { EMBEDDED, api } from "./lib/api";
import { LoginPage } from "./pages/LoginPage";
import { DashboardPage } from "./pages/DashboardPage";
import { RecordsPage, RecordDetailPage } from "./pages/RecordsPage";
import { DealsPage } from "./pages/DealsPage";
import { CollectionPage } from "./pages/CollectionPage";
import { MarketingPage, JourneyDetailPage } from "./pages/MarketingPage";
import { InboxPage } from "./pages/InboxPage";
import { AutomationPage } from "./pages/AutomationPage";
import { ReportsPage } from "./pages/ReportsPage";
import { ImportsPage } from "./pages/ImportsPage";
import { ObjectStudioPage } from "./pages/ObjectStudioPage";
import { SettingsPage } from "./pages/SettingsPage";
import { OperationsPage } from "./pages/OperationsPage";
import { SegmentsPage } from "./pages/SegmentsPage";
import { GrowthAdvisorPage } from "./pages/GrowthAdvisorPage";
import { SalonFunnelPage } from "./pages/SalonFunnelPage";
import { ChannelFunnelPage } from "./pages/ChannelFunnelPage";
import { SetupPage } from "./pages/SetupPage";

function Protected() {
  const location = useLocation();
  const session = useQuery({ queryKey: ["session"], queryFn: () => api<{ authenticated: boolean }>("/api/auth/me"), retry: false });
  const setup = useQuery({
    queryKey: ["setup-status"],
    queryFn: () => api<{ completed: boolean }>("/api/v1/setup/status"),
    retry: false,
    enabled: session.isSuccess,
  });
  if (session.isPending) return <div className="fullscreen"><Loading /></div>;
  if (session.isError && EMBEDDED) {
    window.location.assign(`/admin/login?redirect=${encodeURIComponent(window.location.pathname)}`);
    return <div className="fullscreen"><Loading /></div>;
  }
  if (session.isError) return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  if (setup.isPending) return <div className="fullscreen"><Loading /></div>;
  if (setup.data && !setup.data.completed && location.pathname !== "/setup") return <Navigate to="/setup" replace />;
  return <Shell />;
}

export function App() {
  return <Routes>
    {!EMBEDDED && <Route path="/login" element={<LoginPage />} />}
    <Route element={<Protected />}>
      <Route path="/setup" element={<SetupPage />} />
      <Route path="/" element={<DashboardPage />} />
      <Route path="/contacts" element={<RecordsPage objectKey="contact" />} />
      <Route path="/contacts/:id" element={<RecordDetailPage />} />
      <Route path="/organizations" element={<RecordsPage objectKey="organization" />} />
      <Route path="/organizations/:id" element={<RecordDetailPage />} />
      <Route path="/deals" element={<DealsPage />} />
      <Route path="/deals/:id" element={<RecordDetailPage />} />
      <Route path="/activities" element={<CollectionPage collection="activities" title="Activities" description="A chronological ledger of every meaningful customer interaction." />} />
      <Route path="/tasks" element={<CollectionPage collection="tasks" title="Tasks" description="Prioritized work across sales, service and lifecycle operations." />} />
      <Route path="/segments" element={<SegmentsPage />} />
      <Route path="/campaigns" element={<MarketingPage mode="campaigns" />} />
      <Route path="/journeys" element={<MarketingPage mode="journeys" />} />
      <Route path="/journeys/:id" element={<JourneyDetailPage />} />
      <Route path="/inbox" element={<InboxPage />} />
      <Route path="/automations" element={<AutomationPage />} />
      <Route path="/reports" element={<ReportsPage />} />
      <Route path="/growth-advisor" element={<GrowthAdvisorPage />} />
      <Route path="/salon-funnel" element={<SalonFunnelPage />} />
      <Route path="/channel-funnel" element={<ChannelFunnelPage />} />
      <Route path="/operations" element={<OperationsPage />} />
      <Route path="/imports" element={<ImportsPage />} />
      <Route path="/object-studio" element={<ObjectStudioPage />} />
      <Route path="/settings" element={<SettingsPage />} />
    </Route>
    <Route path="*" element={<Navigate to="/" replace />} />
  </Routes>;
}
